import ApiConfig from "../../Utils/Configs/apiConfig";
import { mapRbacConfig } from "./rbac.mapper";
import { RbacConfig } from "./rbac.types";
import {
  getRoleIdByName,
  UserDetails,
  mapUserDetails,
  ClientSearchResult,
  mapBusinessClient,
} from "./userManagement.mapper";

// Temporary — for testing against the shape before the real endpoint is ready.
const mockRbacConfigResponse0 = {
  currentUser: {
    id: "9d4f6b9e",
    email: "admin.owner@company.com",
    firstName: "John",
    lastName: "Doe",
    role: {
      id: 1,
      name: "AdminOwner",
    },
  },
  permissions: {
    userManagement: {
      view: true,
      create: true,
      edit: true,
      delete: true,
      invite: true,
    },
    clientManagement: {
      view: true,
      create: true,
      edit: true,
      delete: false,
    },
  },
  assignableRoles: [
      {
      id: 1,
      name: "Admin Owner",
    },
    {
      id: 2,
      name: "Admin",
    },
    {
      id: 3,
      name: "StandardUser",
    },
     {
      id: 4,
      name: "testuser",
    },
  ],
  users: [
    {
      id: "1",
      firstName: "Jane",
      lastName: "Smith",
      email: "admin@company.com",
      role: {
        id: 2,
        name: "Admin",
      },
      canEdit: true,
      canDelete: true,
    },
    {
      id: "2",
      firstName: "Michael",
      lastName: "Brown",
      email: "user@company.com",
      role: {
        id: 3,
        name: "StandardUser",
      },
      canEdit: true,
      canDelete: true,
    },
  ],
};


export interface RbacConfigFilters {
  searchTerm?: string;
  roleIds?: number[];
  statuses?: number[];
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export const GetRbacConfigService = async (
  filters: RbacConfigFilters = {},
): Promise<RbacConfig | null> => {
  try {
    // Mock data for testing purposes. Remove this line when the real API is ready.
    // return mapRbacConfig(mockRbacConfigResponse0);

    const query = new URLSearchParams();
    const searchTerm = filters.searchTerm?.trim();

    if (searchTerm) query.set("SearchTerm", searchTerm);
    filters.roleIds?.forEach((roleId) => query.append("RoleIds", String(roleId)));
    filters.statuses?.forEach((status) => query.append("Statuses", String(status)));
    if (filters.sortBy) query.set("SortBy", filters.sortBy);
    if (filters.sortBy && filters.sortOrder) {
      query.set("SortOrder", filters.sortOrder);
    }

    const queryString = query.toString();
    const response = await ApiConfig.getInstance().get(
      queryString ? `/User/bootstrap?${queryString}` : "/User/bootstrap",
    );
    const data = response?.data?.data ?? response?.data;
    return mapRbacConfig(data);
  } catch (error) {
    console.error("Error fetching RBAC config:", error);
    return null;
  }
};

export interface CreateUserPayload {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  client: string[];
}

export const CreateUserService = async (payload: CreateUserPayload) => {
  try {
const payloadFormatted = {
  firstName: payload.firstName,
  lastName: payload.lastName,
  email: payload.email,
  roleId: getRoleIdByName(payload.role),
  clientIds: payload.client, // Assuming the backend expects 'clientIds' instead of 'client'
};

    const response = await ApiConfig.getInstance().post(
      "/User/invite-user",
      payloadFormatted,
    );

    return {
      success: true,
      data: response.data?.data ?? null,
      message: response.data?.message ?? "User created successfully",
    };
  } catch (error: any) {
    console.error("Error creating user:", error);
    return {
      success: false,
      data: null,
      message: error?.response?.data?.message ?? "Failed to create user",
    };
  }
};

// Fetches the full details for one user by id — used to pre-fill the Edit
// User form, since the table row itself only carries a display-friendly
// subset (e.g. no assigned client ids).
export const GetUserByIdService = async (
  userId: string,
): Promise<UserDetails | null> => {
  try {
    const response = await ApiConfig.getInstance().get(`/User/${userId}`);
    const data = response?.data?.data ?? response?.data;

    if (!data) return null;
   const userIdInjected = {
  ...data,
  userId,
};
    // Add User ID to the data. 
    return mapUserDetails(userIdInjected);
  } catch (error) {
    console.error("Error fetching user by id:", error);
    return null;
  }
};

export interface EditUserPayload {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  client: string[];
}

export const EditUserService = async (payload: EditUserPayload) => {
  try {
    // Body shape confirmed against a real request: roleId is a single
    // number (not an array — that was a bug), clientIds is a string[].
    const payloadFormatted = {
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      roleId: getRoleIdByName(payload.role),
      clientIds: payload.client,
    };
  

    // userId is a query param here, not a path segment (unlike
    // GetUserByIdService's GET /User/{userId}).
    // const response = await ApiConfig.getInstance().put(
    //   `/User/${payload.userId}`,
    //   payloadFormatted,
    // );

  const response = await ApiConfig.getInstance().put(
  "/User",
  payloadFormatted,
  {
    params: {
      userId: payload.userId,
    },
  }
);
    return {
      success: true,
      data: response.data?.data ?? null,
      message: response.data?.message ?? "User updated successfully",
    };
  } catch (error: any) {
    console.error("Error updating user:", error);
    return {
      success: false,
      data: null,
      message: error?.response?.data?.message ?? "Failed to update user",
    };
  }
};

export const DeleteUserService = async (userId: string) => {
  try {
    // Endpoint not confirmed yet — fill in once known. Payload is just the
    // userId, sent as the DELETE request body.
    const response = await ApiConfig.getInstance().delete(`/User/${userId}`, {
      data: { userId },
    });

    return {
      success: true,
      data: response.data?.data ?? null,
      message: response.data?.message ?? "User deleted successfully",
    };
  } catch (error: any) {
    console.error("Error deleting user:", error);
    return {
      success: false,
      data: null,
      message: error?.response?.data?.message ?? "Failed to delete user",
    };
  }
};

export const UpdateUserStatusService = async (
  userId: string,
  userStatus: number,
) => {
  try {
    const response = await ApiConfig.getInstance().patch(
      `/User/${encodeURIComponent(userId)}/status`,
      null,
      { params: { userStatus } },
    );

    return {
      success: true,
      data: response.data?.data ?? null,
      message: response.data?.message ?? "User status updated successfully",
    };
  } catch (error: any) {
    console.error("Error updating user status:", error);

    return {
      success: false,
      data: null,
      message:
        error?.response?.data?.message ?? "Failed to update user status",
    };
  }
};

export const DeactivateUserService = {
  deactivate: (userId: string) => UpdateUserStatusService(userId, 2),
  activate: (userId: string) => UpdateUserStatusService(userId, 1),
};


// The real, full client list — used to seed the "Assign Clients" options on
// both the Add User and Edit User forms.
export const GetAllClientsListService = async (): Promise<
  ClientSearchResult[]
> => {
  try {
    const response = await ApiConfig.getInstance().get(
      "/BusinessClient/get-all-businessclient",
    );
    const data = response?.data?.data ?? response?.data ?? [];

    return Array.isArray(data) ? data.map(mapBusinessClient) : [];
  } catch (error) {
    console.error("Error fetching clients list:", error);
    return [];
  }
};

export const ResendInviteServiceUser = async (
  userId: string,
): Promise<any> => {
 
  try {
    const response = await ApiConfig.getInstance().post(
      `/User/${userId}/resend-invite`,
    );

    return response.data;
  } catch (error) {
    console.error("Error resending invite:", error);
    return null;
  }
};